import React from "react";
import { ShieldCheck, Clock, Server, Lock, Video, CircleQuestionMark } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqData = [{
  id: "item-1",
  question: "Ποιος είναι ο χρόνος απόκρισης σε τεχνικά προβλήματα;",
  answer: <>Στη <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo_v3.png" alt="Company Logo" className="h-7 inline-block align-middle" /> κατανοούμε ότι κάθε λεπτό μετράει. Για κρίσιμα ζητήματα που επηρεάζουν τη λειτουργία της επιχείρησής σας, η απόκρισή μας είναι άμεση. Για λιγότερο επείγοντα θέματα, εγγυόμαστε τεχνική υποστήριξη εντός λίγων ωρών.</>,
  icon: <Clock className="w-5 h-5 text-cyan-400" />
}, {
  id: "item-2",
  question: "Πώς διασφαλίζετε τα δεδομένα της επιχείρησής μου;",
  answer: "Εφαρμόζουμε πρωτόκολλα 'Cyber-Secure' που περιλαμβάνουν κρυπτογράφηση επόμενης γενιάς, προηγμένα firewalls και αυτοματοποιημένα cloud backups, διασφαλίζοντας ότι η ψηφιακή σας περιουσία παραμένει απρόσβλητη.",
  icon: <Lock className="w-5 h-5 text-cyan-400" />
}, {
  id: "item-3",
  question: "Αναλαμβάνετε την εγκατάσταση καμερών και συναγερμών;",
  answer: "Ναι, προσφέρουμε ολοκληρωμένες λύσεις φυσικής ασφάλειας. Η ομάδα μας μελετά και εγκαθιστά συστήματα CCTV και συναγερμών υψηλής τεχνολογίας, τα οποία διαχειρίζεστε εύκολα μέσω smartphone ή tablet.",
  icon: <Video className="w-5 h-5 text-cyan-400" />
}, {
  id: "item-4",
  question: "Υποστηρίζετε μικρά γραφεία και νέες επιχειρήσεις;",
  answer: "Απολύτως. Εξειδικευόμαστε στην υποστήριξη μικρών γραφείων, παρέχοντας enterprise-level τεχνολογία σε κλίμακα και κόστος που ταιριάζει απόλυτα στις ανάγκες μιας αναπτυσσόμενης επιχείρησης.",
  icon: <ShieldCheck className="w-5 h-5 text-cyan-400" />
}, {
  id: "item-5",
  question: "Τι περιλαμβάνει η διαχείριση servers και δικτύου;",
  answer: "Καλύπτουμε τα πάντα: από το στήσιμο του hardware και τη βελτιστοποίηση του Wi-Fi, μέχρι την προληπτική παρακολούθηση (monitoring) για τον εντοπισμό σφαλμάτων πριν αυτά προκαλέσουν διακοπή εργασιών.",
  icon: <Server className="w-5 h-5 text-cyan-400" />
}];
export default function FaqSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="42115" id="συχνές-ερωτήσεις" className="dark bg-[#020617] text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center justify-center p-2 bg-cyan-500/10 rounded-full mb-4 border border-cyan-500/20">
              <CircleQuestionMark className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter uppercase font-sans">Συχνες Ερωτησεις</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto leading-relaxed font-sans">
              Βρείτε απαντήσεις για την τεχνική υποστήριξη, την ασφάλεια δικτύων και τις ψηφιακές υποδομές της <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo_v3.png" alt="Company Logo" className="h-7 inline-block align-middle" />.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border border-white/10 rounded-2xl px-6 bg-slate-900/40 backdrop-blur-xl transition-all duration-300 data-[state=open]:border-cyan-500/50 data-[state=open]:bg-slate-900/60 data-[state=open]:shadow-[0_0_40px_-15px_rgba(6,182,212,0.4)] hover:border-cyan-500/30">
                <AccordionTrigger className="text-left hover:no-underline py-6 group">
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500 group-hover:text-cyan-400 transition-colors duration-300">
                      {item.icon}
                    </span>
                    <span className="text-base md:text-lg font-semibold tracking-tight text-slate-100 group-hover:text-cyan-300 transition-colors duration-300 font-sans">
                      {item.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-base leading-relaxed pb-6 pl-[3.25rem] font-sans">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-slate-500 mb-6 font-mono text-sm uppercase tracking-widest">
              Χρειάζεστε περισσότερες διευκρινίσεις;
            </p>
            <Button asChild size="lg" className="rounded-full px-10 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20 border-none">
              <Link to="/ContactPage">Επικοινωνία</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}